var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import PropTypes from 'prop-types';
import { PortableText } from '@portabletext/react';
import classNames from 'classnames';
import { Card } from '@johnlewispartnership/wtr-ingredients/dist/Card';
import { usePromoClickEvent } from '../../hooks/use-promo-click-event';
import cqResponsivePropType from '../../constants/data-shapes/cq-responsive';
import analyticsPropType from '../../constants/data-shapes/analytics';
import { RICH_TEXT_NAME } from '../../constants/analyticsNames';
import { anchor as Anchor, bold, heading1, heading2, heading3, heading4, heading5, heading6, italics, listItem, orderedList, paragraph as Paragraph, span, tableBody, tableCaption, tableCell, tableContainer, tableHeading, tableRow, underline, unorderedList, } from '../../components/RichText/serializers';
import { richTextNode } from '../../constants/types/rich-text';
import { RICH_TEXT } from '../../constants/cmsResourceTypes';
import { getNewLineCharacterCount, richTextSerializer } from './utils';
import styles from './RichText.module.scss';
const RichText = ({ analytics, analyticsTags, blocks, componentId, cqResponsiveClasses, noMargin = false, styleIds, valid = true, position, removeEmptyParagraphs = false, }) => {
    const promoClickEvent = usePromoClickEvent();
    if (!blocks || !blocks.length || !valid)
        return null;
    const onRichTextClick = link => {
        const _a = analytics || {}, { promoName } = _a, remainingAnalyticsProps = __rest(_a, ["promoName"]);
        promoClickEvent(Object.assign(Object.assign(Object.assign({}, link), remainingAnalyticsProps), { creative: 'Rich Text', id: componentId, name: promoName, position }));
    };
    const serializers = {
        unknownMark: () => null,
        unknownType: () => null,
        unknownBlockStyle: () => null,
        unknownList: () => null,
        unknownListItem: () => null,
        marks: {
            span: ({ text }) => {
                const numberOfNewLines = getNewLineCharacterCount(text);
                return (React.createElement(React.Fragment, null, [...Array(numberOfNewLines - 1)].map(() => (React.createElement("br", null)))));
            },
        },
        block: {
            anchor: props => React.createElement(Anchor, Object.assign({}, props, { onRichTextClick: onRichTextClick })),
            bold,
            heading1,
            heading2,
            heading3,
            heading4,
            heading5,
            heading6,
            italics,
            listItem,
            orderedList,
            paragraph: props => React.createElement(Paragraph, Object.assign({}, props, { removeEmptyParagraphs: removeEmptyParagraphs })),
            span,
            tableBody,
            tableCaption,
            tableCell,
            tableContainer,
            tableHeading,
            tableRow,
            underline,
            unorderedList,
        },
    };
    const richTextBlocks = richTextSerializer(blocks);
    const onWhiteBackground = styleIds && styleIds.includes('faq_landing_page_frequent_questions_card');
    return onWhiteBackground ? (React.createElement("section", Object.assign({ className: classNames(cqResponsiveClasses, styles.richText, {
            [styles.noMargin]: noMargin,
        }) }, analyticsTags),
        React.createElement(Card, { "data-testid": "rich-text-card", className: styles.noMargin },
            React.createElement(PortableText, { value: richTextBlocks, components: serializers, onMissingComponent: false })))) : (React.createElement("section", Object.assign({ className: classNames(cqResponsiveClasses, styles.richText, {
            [styles.noMargin]: noMargin,
        }) }, analyticsTags),
        React.createElement(PortableText, { value: richTextBlocks, components: serializers, onMissingComponent: false })));
};
RichText.displayName = RICH_TEXT_NAME;
RichText.resourceType = RICH_TEXT;
RichText.defaultProps = {
    analytics: {},
    analyticsTags: undefined,
    blocks: [],
    componentId: '',
    cqResponsive: {},
    cqResponsiveClasses: undefined,
    noMargin: false,
    styleIds: [],
    valid: true,
    removeEmptyParagraphs: false,
};
RichText.propTypes = {
    analytics: analyticsPropType(),
    analyticsTags: PropTypes.shape({}),
    blocks: PropTypes.arrayOf(richTextNode),
    componentId: PropTypes.string,
    cqResponsive: cqResponsivePropType(),
    cqResponsiveClasses: PropTypes.arrayOf(PropTypes.string),
    noMargin: PropTypes.bool,
    styleIds: PropTypes.arrayOf(PropTypes.string),
    valid: PropTypes.bool,
    position: PropTypes.string.isRequired,
    removeEmptyParagraphs: PropTypes.bool,
};
export default RichText;
//# sourceMappingURL=index.js.map